import { Banner } from '@figma/fpl-components';
import { color as fplColor } from '@figma/fpl-tokens/tokens.stylex';
import { AppDialogActions } from '@/UI/AppDialog/AppDialog';
import { EL_COLORS } from '@/colors';
import { TextSmMd, TextSmRg, TextStdMd } from '@/UI/AppTypography/AppTypography';
import { FlexCenVer } from '@/UI/styles';
import { InfoIcon } from '@/UI/Icons';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import type { SxProps, Theme } from '@/UI/theme.exports';

interface ShareScopeNoteProps {
  /** The boundary itself. Carries the design's strong weight. */
  lead: string;
  /** Where to go for the half this modal does not cover. */
  detail: string;
}

/**
 * The share modals' footer banner (design 85287:23242). A tool and its workflow carry separate
 * access lists, and the two modals look alike enough that sharing one reads as sharing both — so
 * each names its own boundary and points at the control that covers the other half. Shared so the
 * two cannot drift apart; only the copy differs.
 *
 * `space-between` is what picks the footer variant that holds left-aligned content. The text takes
 * its color by inheritance, which is `--color-text` under FPL and the dialog's own on the legacy
 * theme, where the FPL variables are absent.
 */
export function ShareScopeNote({ lead, detail }: ShareScopeNoteProps) {
  const isFpl = useFplMigrationFlag();

  return (
    <AppDialogActions
      sx={{
        justifyContent: 'space-between',
        // FPL's footer rules itself off from the body; the legacy one has to be told to.
        ...(isFpl ? undefined : { borderTop: `1px solid ${EL_COLORS.BoxBorder}` }),
      }}
    >
      {/* Lands the info glyph on the same column as the row icons above it. Each footer starts from a
        different inset of its own — 12px under FPL, 8px on MUI — so each needs its own correction. */}
      <ShareScopeNoteLine lead={lead} detail={detail} sx={{ ml: isFpl ? '-4px' : 0, pl: isFpl ? 0 : 0.5 }} />
    </AppDialogActions>
  );
}

/** The note without its footer, for a modal that shows it somewhere else. */
export function ShareScopeNoteLine({ lead, detail, sx }: ShareScopeNoteProps & { sx?: SxProps<Theme> }) {
  const isFpl = useFplMigrationFlag();
  // Under FPL both variants are body/medium and differ only in weight, which is the design. On the
  // legacy theme `TextStdMd` is two steps larger than `TextSmRg`, so the sentence would break in the
  // middle; there the pair that shares a size is `TextSmMd`/`TextSmRg`.
  const Lead = isFpl ? TextStdMd : TextSmMd;

  return (
    <FlexCenVer sx={[{ gap: 0.5, py: 0.5 }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}>
      {/* The FPL glyph draws small inside a 24px box. The legacy one fills it, so it is sized down
        to sit with 12px text. */}
      <InfoIcon {...(isFpl ? {} : { width: 16, height: 16 })} />
      {/* Legacy body text is 12px against FPL's 11px, so the two sentences only make one line
        there on a tighter gap. `wrap` is the fallback for copy that still cannot fit. */}
      <FlexCenVer sx={{ gap: isFpl ? 1 : 0.5, flexWrap: 'wrap' }}>
        <Lead>{lead}</Lead>
        <TextSmRg>{detail}</TextSmRg>
      </FlexCenVer>
    </FlexCenVer>
  );
}

/**
 * The banner for a workflow opened to its tool's audience (design 86451:55026): every invite to the
 * tool also opens the workflow, so both modals warn about it in the same words.
 */
export function ShareExposedNote({ title, detail }: { title: string; detail: string }) {
  return (
    <Banner.Inline variant="default">
      <Banner.Message title={title}>
        <span style={{ color: fplColor.textSecondary }}>{detail}</span>
      </Banner.Message>
    </Banner.Inline>
  );
}
